import { Fragment } from 'react';
import { ActionButton, EmptyState, ExportDropdown, SectionCard } from '@practium/ui';
import { PLAN_SECTION_LABELS } from '../../constants/parameters.constants';
import type { FertilizationPlanReferenceDTO, PlanSectionKey, PlanSectionProps } from '../../types/parameters.types';
import {
	PlanEfficiencySection,
	PlanExportsSection,
	PlanExtractionSection,
	PlanMinimumSection,
	PlanNutrientsSection,
	PlanRootSection,
} from '../Plan/PlanSections';
import { exportPlanToExcel, exportPlanToPdf } from '../../utils/parametersExport';

const SECTION_KEYS = Object.keys(PLAN_SECTION_LABELS) as PlanSectionKey[];

interface PlanReferenceCardProps {
	plan: FertilizationPlanReferenceDTO | null;
	section: PlanSectionKey;
	isLoading: boolean;
	isError: boolean;
	isSaving: boolean;
	onSectionChange: (section: PlanSectionKey) => void;
	onNewPlan: () => void;
	onSave: PlanSectionProps['onSave'];
}

const renderSection = (section: PlanSectionKey, props: PlanSectionProps) => {
	if (section === 'root') return <PlanRootSection {...props} />;
	if (section === 'extraction') return <PlanExtractionSection {...props} />;
	if (section === 'exports') return <PlanExportsSection {...props} />;
	if (section === 'efficiency') return <PlanEfficiencySection {...props} />;
	if (section === 'minimum') return <PlanMinimumSection {...props} />;
	return <PlanNutrientsSection plan={props.plan} />;
};

export const PlanReferenceCard = ({
	plan,
	section,
	isLoading,
	isError,
	isSaving,
	onSectionChange,
	onNewPlan,
	onSave,
}: PlanReferenceCardProps) => {
	const renderContent = () => {
		if (isError) return <EmptyState title="Erro ao carregar os planos" description="Tente novamente em instantes." />;
		if (!plan) return <EmptyState title="Nenhum plano cadastrado" description="Não há plano de cálculo para exibir." />;
		return <Fragment key={plan._id}>{renderSection(section, { plan, isSaving, onSave })}</Fragment>;
	};

	return (
		<SectionCard
			title={plan?.name ?? 'Plano de cálculo'}
			subtitle={
				plan && !plan.isActive
					? 'Plano inativo: alterações aqui não afetam o cálculo até ele ser ativado'
					: 'Coeficientes usados no cálculo do plano de fertilização'
			}
			tabs={SECTION_KEYS.map((key) => ({
				key,
				label: PLAN_SECTION_LABELS[key],
				content: key === section ? renderContent() : null,
			}))}
			activeTab={section}
			onTabChange={onSectionChange}
			isLoading={isLoading}
			headerAction={
				plan && (
					<>
						<ActionButton text="Novo plano" onClick={onNewPlan} />
						<ExportDropdown onExportExcel={() => exportPlanToExcel(plan)} onExportPdf={() => exportPlanToPdf(plan)} />
					</>
				)
			}
		/>
	);
};
